import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import html2canvas from 'html2canvas';

import { Strategy } from '../../../types/strategy';

declare global {
  interface Window {
    FintargetWidget?: {
      init: (config: { containerId: string; strategyId: string }) => void;
    };
  }
}

const WIDGET_SCRIPT_URL = 'https://cdn.fintarget.ru/widget/fintarget-widget.js';
const WIDGET_CSS_URL = 'https://cdn.fintarget.ru/widget/fintarget-widget.css';
const WIDGET_CONTAINER_ID = 'fintarget-widget-preview';

type Props = {
  strategies: Strategy[];
};

const getStrategySlug = (fintargetUrl: string): string => {
  try {
    const url = new URL(fintargetUrl);
    const pathParts = url.pathname.split('/').filter(Boolean);
    return pathParts[pathParts.length - 1] || '';
  } catch {
    return '';
  }
};

const buildWidgetCode = (strategySlug: string): string =>
  `<!-- Подключение виджета -->
<script src="${WIDGET_SCRIPT_URL}"></script>
<link rel="stylesheet" href="${WIDGET_CSS_URL}" />

<!-- Контейнер для виджета -->
<div id="fintarget-chart"></div>

<!-- Инициализация -->
<script>
FintargetWidget.init({
    containerId: 'fintarget-chart',
    strategyId: '${strategySlug}',
});
</script>`;

const loadResource = (tag: 'script' | 'link', url: string): Promise<void> => {
  const selector = tag === 'script' ? `script[src="${url}"]` : `link[href="${url}"]`;
  if (document.querySelector(selector)) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const el = document.createElement(tag);

    if (tag === 'script') {
      (el as HTMLScriptElement).src = url;
    } else {
      (el as HTMLLinkElement).rel = 'stylesheet';
      (el as HTMLLinkElement).href = url;
    }

    el.onload = () => resolve();
    el.onerror = reject;
    document.head.appendChild(el);
  });
};

export const WidgetCodeGenerator: React.FC<Props> = ({ strategies }) => {
  const [selectedStrategyId, setSelectedStrategyId] = useState<string>('');
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [isWidgetLoading, setIsWidgetLoading] = useState(false);
  const [widgetInstance, setWidgetInstance] = useState(0);

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const currentContainerId = `${WIDGET_CONTAINER_ID}-${widgetInstance}`;

  const availableStrategies = useMemo(
    () => strategies.filter((s) => s.fintargetUrl && getStrategySlug(s.fintargetUrl)),
    [strategies],
  );

  const selectedStrategy = useMemo(
    () => availableStrategies.find((s) => s.id === selectedStrategyId),
    [availableStrategies, selectedStrategyId],
  );

  const strategySlug = selectedStrategy ? getStrategySlug(selectedStrategy.fintargetUrl) : '';
  const widgetCode = useMemo(() => (strategySlug ? buildWidgetCode(strategySlug) : ''), [strategySlug]);

  useEffect(() => {
    if (!strategySlug) return;

    const container = chartContainerRef.current;
    if (!container) return;

    let cancelled = false;
    setIsWidgetLoading(true);

    const initWidget = async () => {
      try {
        await Promise.all([loadResource('script', WIDGET_SCRIPT_URL), loadResource('link', WIDGET_CSS_URL)]);

        if (cancelled) return;

        window.FintargetWidget?.init({
          containerId: currentContainerId,
          strategyId: strategySlug,
        });
      } catch (error) {
        console.error('Failed to load widget resources:', error);
      } finally {
        if (!cancelled) setIsWidgetLoading(false);
      }
    };

    initWidget();

    return () => {
      cancelled = true;
    };
  }, [strategySlug, currentContainerId]);

  const handleStrategyChange = (event: SelectChangeEvent<string>) => {
    setSelectedStrategyId(event.target.value);
    setWidgetInstance((prev) => prev + 1);
  };

  const handleCopyCode = useCallback(async () => {
    if (!widgetCode) return;

    try {
      await navigator.clipboard.writeText(widgetCode);
      setSnackbarMessage('Код скопирован в буфер обмена');
    } catch (error) {
      console.error('Failed to copy code:', error);
    }
  }, [widgetCode]);

  const handleCopyChartAsImage = useCallback(async () => {
    const container = chartContainerRef.current;
    if (!container) return;

    setIsCapturing(true);
    try {
      const canvas = await html2canvas(container, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png');
      });

      try {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        setSnackbarMessage('График скопирован в буфер обмена как картинка');
      } catch {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `strategy-chart-${strategySlug}.png`;
        link.click();
        URL.revokeObjectURL(url);
        setSnackbarMessage('График сохранён как файл');
      }
    } catch (error) {
      console.error('Failed to capture chart:', error);
      setSnackbarMessage('Не удалось захватить график');
    } finally {
      setIsCapturing(false);
    }
  }, [strategySlug]);

  const handleCloseSnackbar = () => {
    setSnackbarMessage('');
  };

  if (availableStrategies.length === 0) {
    return null;
  }

  return (
    <Card sx={{ mt: 4 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Виджет для встраивания на сайт
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Выберите стратегию, чтобы увидеть виджет и скопировать код для встраивания на ваш сайт
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel id="widget-strategy-select-label">Выберите стратегию</InputLabel>
            <Select
              labelId="widget-strategy-select-label"
              id="widget-strategy-select"
              value={selectedStrategyId}
              label="Выберите стратегию"
              onChange={handleStrategyChange}
              aria-label="Выбор стратегии для виджета"
            >
              {availableStrategies.map((strategy) => (
                <MenuItem key={strategy.id} value={strategy.id}>
                  {strategy.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {strategySlug && (
            <Button
              variant="outlined"
              startIcon={isCapturing ? <CircularProgress size={18} /> : <CameraAltOutlinedIcon />}
              onClick={handleCopyChartAsImage}
              disabled={isCapturing || isWidgetLoading}
              aria-label="Скопировать график как картинку"
              sx={{ ml: 'auto', whiteSpace: 'nowrap' }}
            >
              {isCapturing ? 'Захват...' : 'Скопировать график как картинку'}
            </Button>
          )}
        </Box>

        {strategySlug && (
          <>
            <Box
              sx={{
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                position: 'relative',
                minHeight: 300,
              }}
            >
              {isWidgetLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'rgba(255,255,255,0.7)',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              <Box
                ref={chartContainerRef}
                key={currentContainerId}
                id={currentContainerId}
                sx={{ width: '100%', minHeight: 300 }}
              />
            </Box>

            <Box
              sx={{
                position: 'relative',
                backgroundColor: '#1e1e1e',
                borderRadius: 1,
                p: 2,
                overflow: 'auto',
              }}
            >
              <Tooltip title="Копировать код">
                <IconButton
                  onClick={handleCopyCode}
                  aria-label="Копировать код виджета"
                  tabIndex={0}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Box
                component="pre"
                sx={{
                  margin: 0,
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  lineHeight: 1.5,
                  color: '#d4d4d4',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                <Box component="code">{widgetCode}</Box>
              </Box>
            </Box>
          </>
        )}
      </CardContent>

      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Card>
  );
};
